import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { Quake } from '../api/types';

interface DisplayedMoversOptions {
  autoRefresh: boolean;
  intervalSeconds: number;
  resetKey: string;
}

export function useDisplayedMovers(incoming: readonly Quake[], options: DisplayedMoversOptions) {
  const [displayed, setDisplayed] = useState<Quake[]>(() => [...incoming]);
  const [lastAppliedAt, setLastAppliedAt] = useState<Date>(() => new Date());
  const [pendingUpdates, setPendingUpdates] = useState(0);
  const lastIncomingFingerprint = useRef(fingerprint(incoming));

  const displayedFingerprint = useMemo(() => fingerprint(displayed), [displayed]);
  const incomingFingerprint = useMemo(() => fingerprint(incoming), [incoming]);

  const applyNow = useCallback(() => {
    setDisplayed([...incoming]);
    setLastAppliedAt(new Date());
    setPendingUpdates(0);
    lastIncomingFingerprint.current = incomingFingerprint;
  }, [incoming, incomingFingerprint]);

  useEffect(() => {
    setDisplayed([...incoming]);
    setLastAppliedAt(new Date());
    setPendingUpdates(0);
    lastIncomingFingerprint.current = incomingFingerprint;
  }, [options.resetKey]);

  useEffect(() => {
    // When auto-refresh is on, the user wants live data. Apply the
    // incoming snapshot as soon as the polling tick lands; do not
    // accumulate a "pending" counter. Pending only matters when the
    // user has explicitly paused the feed.
    if (options.autoRefresh) {
      if (incomingFingerprint === displayedFingerprint) {
        return;
      }
      applyNow();
      return;
    }

    if (displayed.length === 0 && incoming.length > 0) {
      applyNow();
      return;
    }

    if (incomingFingerprint === displayedFingerprint) {
      setPendingUpdates(0);
      lastIncomingFingerprint.current = incomingFingerprint;
      return;
    }

    if (incomingFingerprint !== lastIncomingFingerprint.current) {
      setPendingUpdates((value) => Math.min(value + 1, 99));
      lastIncomingFingerprint.current = incomingFingerprint;
    }
  }, [
    applyNow,
    displayed.length,
    displayedFingerprint,
    incoming.length,
    incomingFingerprint,
    lastAppliedAt,
    options.autoRefresh,
    options.intervalSeconds,
  ]);

  return {
    displayed,
    lastAppliedAt,
    pendingUpdates,
    hasPendingUpdates: pendingUpdates > 0 && incomingFingerprint !== displayedFingerprint,
    applyNow,
  };
}

function fingerprint(items: readonly Quake[]) {
  return items
    .map((item) =>
      [
        item.market_id || item.asset_id,
        item.asset_id,
        item.timestamp,
        item.new_price.toFixed(4),
        item.change_pp.toFixed(2),
        item.score.toFixed(2),
      ].join(':'),
    )
    .join('|');
}
